import { Fragment, memo } from 'react';

import { Paragraph, rem } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import { createDomName } from '../../../../utils/dom/identifying';
import { inline } from '../../../../utils/general/lambda';
import { EMOJI_FONT_FAMILY } from '../../../../utils/styling/layouts';

interface IChatMessageContentProps extends IBaseDomElementProps {
  messageText: string;
}

export const ChatMessageContent = memo(function ({
  className = createDomName('chat-message-content'),
  messageText,
}: IChatMessageContentProps) {
  const linksInMessageText = [...messageText.matchAll(interactionToolsConfig.REGEXP.DEFAULT_LINK)];

  const messageContent = inline(() => {
    if (linksInMessageText.length > 0) {
      const elements: (string | JSX.Element)[] = [];
      let lastIndex = 0;

      linksInMessageText.forEach((match) => {
        // Text before first link match
        if (match.index && match.index > lastIndex) {
          elements.push(messageText.substring(lastIndex, match.index));
        }

        const link = match[0];
        const hasProtocol = link.search(interactionToolsConfig.REGEXP.LINK_PROTOCOL) === 0;
        const linkUrl = hasProtocol ? link : `https://${link}`;

        elements.push(
          <a href={linkUrl} target={'_blank'} rel={'noreferrer noopener'}>
            {link}
          </a>,
        );

        lastIndex = (match.index as number) + link.length;
      });

      // Text after last link match
      if (messageText.length > lastIndex) {
        elements.push(messageText.substring(lastIndex));
      }

      return elements.map((el, i) => <Fragment key={i}>{el}</Fragment>);
    }

    return messageText;
  });

  return (
    <Paragraph
      className={className}
      letterSpacing={rem(0.16)}
      lineHeight={1.5}
      color={'text-secondary'}
      fontSize={rem(14)}
      marginBottom={rem(4)}
      fontFamily={EMOJI_FONT_FAMILY}
      sx={{
        ['a, a:hover, a:active']: {
          color: 'blue.500',
          textDecoration: 'none',
          cursor: 'pointer',
        },
      }}
    >
      {messageContent}
    </Paragraph>
  );
});
